import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Briefcase, MessageSquare, MoreHorizontal } from "lucide-react";
import { useUnreadCount } from "@/hooks/useUnreadCount";
import { usePendingAppointmentsCount } from "@/hooks/usePendingAppointmentsCount";
import { useIsArtisan } from "@/hooks/useArtisanProfile";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import ArtisanDrawerMenu from "./ArtisanDrawerMenu";
import { cn } from "@/lib/utils";

const ArtisanBottomNav = () => {
  const location = useLocation();
  const unreadCount = useUnreadCount();
  const pendingRdv = usePendingAppointmentsCount();
  const { data: isArtisan } = useIsArtisan();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const lPath = useLocalizedPath();

  if (!isArtisan) return null;

  const stripSlash = (p: string) => p.replace(/\/$/, "") || "/";
  const isActive = (path: string) => stripSlash(location.pathname) === stripSlash(path);

  const navItems = [
    {
      to: lPath("/dashboard"),
      icon: LayoutDashboard,
      label: "Dashboard",
    },
    {
      to: lPath("/annonces"),
      icon: Briefcase,
      label: "Missions",
    },
    {
      to: lPath("/messagerie"),
      icon: MessageSquare,
      label: "Messages",
      badge: unreadCount > 0 ? (unreadCount > 99 ? "99+" : String(unreadCount)) : undefined,
      badgeClass: "bg-destructive text-destructive-foreground",
    },
  ];

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/70 bg-card/97 backdrop-blur-md safe-area-bottom md:hidden"
        aria-label="Navigation artisan"
      >
        <div className="flex items-stretch justify-around h-16">
          {navItems.map((item) => {
            const active = isActive(item.to);
            return (
              <Link
                key={item.label}
                to={item.to}
                className={cn(
                  "relative flex flex-1 flex-col items-center justify-center py-2 px-1 min-h-[44px] transition-colors",
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
                aria-label={item.label + (item.badge ? ` (${item.badge})` : "")}
                aria-current={active ? "page" : undefined}
              >
                {active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full bg-primary" aria-hidden="true" />
                )}

                <div className="relative">
                  <item.icon
                    className={cn("transition-all duration-150", active ? "h-6 w-6" : "h-5 w-5")}
                    aria-hidden="true"
                  />
                  {item.badge && (
                    <Badge
                      className={cn(
                        "absolute -top-2 -right-3 h-4 min-w-4 rounded-full px-1 text-[9px] leading-none flex items-center justify-center",
                        item.badgeClass
                      )}
                      aria-hidden="true"
                    >
                      {item.badge}
                    </Badge>
                  )}
                </div>
              </Link>
            );
          })}

          {/* Plus — Profil, RDV, Paramètres, Déconnexion */}
          <button
            onClick={() => setDrawerOpen(true)}
            className={cn(
              "relative flex flex-1 flex-col items-center justify-center py-2 px-1 min-h-[44px] transition-colors",
              drawerOpen ? "text-primary" : "text-muted-foreground hover:text-foreground"
            )}
            aria-label="Plus d'options"
            aria-haspopup="dialog"
          >
            {pendingRdv > 0 && (
              <Badge
                className="absolute top-1.5 right-[calc(50%-14px)] translate-x-full h-4 min-w-4 rounded-full bg-warning px-1 text-[9px] text-warning-foreground leading-none flex items-center justify-center"
                aria-label={`${pendingRdv} RDV en attente`}
              >
                {pendingRdv}
              </Badge>
            )}
            <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </nav>

      <ArtisanDrawerMenu open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  );
};

export default ArtisanBottomNav;
